import { getUserProfile } from '@/api/user';
import {
  getAccessToken,
  getWalletAddress,
  setWalletAddress,
} from '@/utils/auth-token';
import { getPrivyEmbeddedSignerAddress } from '@/utils/privy';
import { sessionExpectsCustodialWallet } from '@/utils/session-login-track';
import {
  addressesEqual,
  fetchTradeAddressFromList,
  getTradeAddressType,
} from '@/utils/trade-address';

function pickWalletFromProfile(profile) {
  if (!profile || typeof profile !== 'object') return '';
  const candidates = [
    profile.walletAddress,
    profile.wallet,
    profile.address,
    profile.ethAddress,
    profile.userAddress,
  ];
  for (const value of candidates) {
    const trimmed = String(value ?? '').trim();
    if (trimmed) return trimmed;
  }
  return '';
}

async function resolvePrivyTradeUserAddress() {
  let embeddedAddress = '';
  try {
    embeddedAddress = String(await getPrivyEmbeddedSignerAddress()).trim();
  } catch (err) {
    console.warn('[wallet] Privy signer unavailable', err);
  }

  if (embeddedAddress) {
    const { address: fromList } = await fetchTradeAddressFromList({
      embeddedAddress,
    });
    const resolved = fromList || embeddedAddress;
    setWalletAddress(resolved);
    return resolved;
  }

  const { address: fromList, records } = await fetchTradeAddressFromList();
  if (fromList) {
    setWalletAddress(fromList);
    return fromList;
  }

  const cached = getWalletAddress();
  if (cached) {
    const isCustodialInList =
      Array.isArray(records) &&
      records.some(
        (item) =>
          addressesEqual(item?.address, cached) && getTradeAddressType(item) === 2
      );
    if (isCustodialInList || !records?.length) {
      return cached;
    }
  }

  return '';
}

/**
 * 解析下单所需钱包地址：prop → Privy signer / trade-address/list (托管) / localStorage / profile (自管)
 * @param {string} [preferred]
 */
export async function resolveTradeUserAddress(preferred = '') {
  const fromPreferred = String(preferred ?? '').trim();

  // 仅按本次 Session 轨分流；禁止用残留 provider=privy 覆盖 WALLET 登录
  if (sessionExpectsCustodialWallet()) {
    let embeddedAddress = '';
    try {
      embeddedAddress = String(await getPrivyEmbeddedSignerAddress()).trim();
    } catch (err) {
      // ignore
    }

    if (
      fromPreferred &&
      (!embeddedAddress || addressesEqual(fromPreferred, embeddedAddress))
    ) {
      return fromPreferred;
    }

    return resolvePrivyTradeUserAddress();
  }

  if (fromPreferred) return fromPreferred;

  const cached = getWalletAddress();
  if (cached) return cached;

  if (!getAccessToken()) return '';

  try {
    const profile = await getUserProfile();
    const fromProfile = pickWalletFromProfile(profile);
    if (fromProfile) {
      setWalletAddress(fromProfile);
      return fromProfile;
    }
  } catch (err) {
    console.warn('[wallet] resolveTradeUserAddress failed', err);
  }

  return '';
}
